/**
 * AuditLogTable Component
 *
 * Table displaying audit log entries with action/outcome badges.
 */

import React from "react";
import { EyeIcon } from "@heroicons/react/24/outline";
import { Button } from "../../../components/ui/Button";
import { TableSkeleton } from "../../../components/ui/TableSkeleton";
import { EmptyState } from "../../../components/ui/EmptyState";
import { ActionBadge, OutcomeBadge } from "./AuditLogActionBadge";
import type { AuditLogEntry } from "../../../types/audit-log.types";

interface AuditLogTableProps {
  entries: AuditLogEntry[];
  isLoading: boolean;
  onView: (entry: AuditLogEntry) => void;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString();
};

const formatResourceType = (type: string) => {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export const AuditLogTable: React.FC<AuditLogTableProps> = ({ entries, isLoading, onView }) => {
  if (isLoading) {
    return <TableSkeleton rows={10} columns={7} />;
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        title="No audit log entries"
        description="No audit log entries found matching your filters."
      />
    );
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actor</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resource</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resource ID</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outcome</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {entries.map((entry) => (
            <tr key={entry.auditId} className="hover:bg-gray-50 cursor-pointer" onClick={() => onView(entry)}>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(entry.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {entry.actorEmail}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <ActionBadge action={entry.action} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {formatResourceType(entry.resourceType)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm font-mono text-gray-500">{entry.resourceId === "UNKNOWN" ? "-" : entry.resourceId.substring(0, 8)}</span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <OutcomeBadge outcome={entry.outcome} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); onView(entry); }} title="View Details">
                  <EyeIcon className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
